import React, { useMemo } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, StyleProp, ViewStyle } from 'react-native';
import { REDESIGN_TYPOGRAPHY, REDESIGN_SPACING } from '@/constants/residents/homeConstants';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { formatCurrency } from '@/utils/common/formatting';
import { useThemeColors } from '@/hooks/residents/useThemeColors';
import { AccountCreditIndicator } from '@/features/payments/components/shared/AccountCreditIndicator';
import { GlassCard } from '../GlassCard';

interface PaymentBalanceDueCardProps {
  balanceDue: number;
  accountCredit?: number;
  /** 'compact' for home screen (property footer + full-width button), 'full' for payments screen (expanded details) */
  variant?: 'compact' | 'full';
  /** 'large' increases padding, amount font size, and adds a blue accent top border for desktop dashboards */
  size?: 'default' | 'large';
  autopayEnabled?: boolean;
  autopayCardInfo?: string;
  autopayDate?: string;
  nextChargeDate?: string;
  recurringChargesCount?: number;
  dueDate?: string;
  isOverdue?: boolean;
  propertyName?: string;
  unitName?: string;
  onMakePayment: () => void;
  onViewBreakdown?: () => void;
  style?: StyleProp<ViewStyle>;
}

export const PaymentBalanceDueCard: React.FC<PaymentBalanceDueCardProps> = React.memo(({
  balanceDue,
  accountCredit = 0,
  variant = 'full',
  size = 'default',
  autopayEnabled = false,
  autopayCardInfo,
  autopayDate,
  nextChargeDate,
  recurringChargesCount = 0,
  dueDate,
  isOverdue = false,
  propertyName,
  unitName,
  onMakePayment,
  onViewBreakdown,
  style,
}) => {
  const { colors, semantic } = useThemeColors();

  const formattedBalance = useMemo(
    () => formatCurrency(Math.abs(balanceDue)),
    [balanceDue],
  );

  const isCompact = variant === 'compact';
  const isLarge = size === 'large';
  const hasBalance = balanceDue > 0;

  return (
    <GlassCard
      style={[isLarge && styles.cardLarge, style]}
      padding={isLarge ? 28 : REDESIGN_SPACING.cardPadding}
    >
      {isLarge && <View style={[styles.accentBorder, { backgroundColor: semantic.interactive.primary }]} />}
      <Text style={[styles.balanceLabel, { color: colors.text.subtle }, isLarge && styles.balanceLabelLarge]}>Balance Due</Text>

      <View style={styles.amountRow}>
        <Text style={[styles.amountText, { color: colors.text.primary }, isLarge && styles.amountTextLarge]}>{formattedBalance}</Text>
        {!isCompact && dueDate && hasBalance && (
          <View style={[styles.dueBadge, { backgroundColor: colors.accent.redBg }, isOverdue && styles.dueBadgeOverdue]}>
            <Text style={[styles.dueBadgeText, { color: colors.accent.red }, isOverdue && { color: colors.accent.red }]}>
              {isOverdue ? 'Past due' : `Due ${dueDate}`}
            </Text>
          </View>
        )}
      </View>

      <AccountCreditIndicator amount={accountCredit} variant="inline" style={styles.accountCredit} />

      {autopayEnabled && (
        <View style={styles.autopayRow}>
          <View style={[styles.autopayCheck, { backgroundColor: colors.accent.greenBg }]}>
            <RESIDENTS_SVG_COMPONENTS.CHECK width={10} height={10} color={colors.accent.green} />
          </View>
          <Text style={[styles.autopayText, { color: colors.accent.green }]}>Autopay active</Text>
          {!isCompact && autopayCardInfo && (
            <Text style={[styles.autopayDetails, { color: colors.text.dimmer }]}>{autopayCardInfo}{autopayDate ? ` · ${autopayDate}` : ''}</Text>
          )}
        </View>
      )}

      <View style={[styles.divider, { backgroundColor: colors.card.border }]} />

      <View style={styles.footer}>
        {isCompact && propertyName ? (
          <Text style={[styles.propertyText, { color: colors.text.dimmer }]}>
            {propertyName}
            {unitName ? <Text style={{ color: colors.text.muted, fontWeight: '500' }}> · {unitName}</Text> : null}
          </Text>
        ) : null}

        {!isCompact && nextChargeDate ? (
          <Text style={[styles.nextChargeText, { color: colors.text.dimmer }]}>
            Next charge <Text style={{ fontWeight: '500', color: colors.text.muted }}>{nextChargeDate}</Text>
          </Text>
        ) : null}

        <TouchableOpacity
          style={[styles.payButtonFullWidth, { backgroundColor: semantic.interactive.primary }]}
          onPress={onMakePayment}
          activeOpacity={0.8}
        >
          <Text style={styles.payButtonText}>Make Payment</Text>
        </TouchableOpacity>

        {!isCompact && recurringChargesCount > 0 && (
          <View style={styles.breakdownRow}>
            <Text style={[styles.breakdownText, { color: colors.text.dim }]}>
              {recurringChargesCount} recurring charge{recurringChargesCount !== 1 ? 's' : ''}
            </Text>
            {onViewBreakdown && (
              <TouchableOpacity onPress={onViewBreakdown} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
                <Text style={[styles.breakdownLink, { color: semantic.interactive.primary }]}>View breakdown</Text>
              </TouchableOpacity>
            )}
          </View>
        )}
      </View>
    </GlassCard>
  );
});

PaymentBalanceDueCard.displayName = 'PaymentBalanceDueCard';

const styles = StyleSheet.create({
  balanceLabel: {
    ...REDESIGN_TYPOGRAPHY.balanceLabel,
    marginBottom: 8,
  },
  amountRow: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    marginBottom: 12,
  },
  amountText: {
    ...REDESIGN_TYPOGRAPHY.amount,
  },
  dueBadge: {
    borderRadius: 6,
    paddingHorizontal: 10,
    paddingVertical: 4,
  },
  dueBadgeOverdue: {
    backgroundColor: 'rgba(245,85,96,0.2)',
  },
  dueBadgeText: {
    fontSize: 12,
    fontWeight: '600',
  },
  accountCredit: {
    paddingHorizontal: 0,
    marginBottom: 12,
  },
  autopayRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 16,
  },
  autopayCheck: {
    width: 16,
    height: 16,
    borderRadius: 4,
    alignItems: 'center',
    justifyContent: 'center',
  },
  autopayText: {
    ...REDESIGN_TYPOGRAPHY.captionMedium,
  },
  autopayDetails: {
    ...REDESIGN_TYPOGRAPHY.caption,
  },
  divider: {
    height: 1,
    marginBottom: 17,
  },
  footer: {
    gap: 12,
  },
  propertyText: {
    ...REDESIGN_TYPOGRAPHY.small,
  },
  nextChargeText: {
    ...REDESIGN_TYPOGRAPHY.small,
  },
  payButtonFullWidth: {
    borderRadius: REDESIGN_SPACING.buttonRadius,
    paddingHorizontal: 28,
    height: REDESIGN_SPACING.buttonHeight,
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
  },
  payButtonText: {
    ...REDESIGN_TYPOGRAPHY.button,
    color: '#FFFFFF',
    textAlign: 'center',
  },
  breakdownRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  breakdownText: {
    ...REDESIGN_TYPOGRAPHY.caption,
  },
  breakdownLink: {
    ...REDESIGN_TYPOGRAPHY.captionMedium,
  },
  cardLarge: {
    overflow: 'hidden',
  },
  accentBorder: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 2,
  },
  balanceLabelLarge: {
    letterSpacing: 1.5,
  },
  amountTextLarge: {
    fontSize: 42,
    lineHeight: 48,
  },
});
